import { getModelNameLabel } from './ChatTypeHelpers';
import {
  AnomalyDirection,
  AnomalyIncident,
  AnomalyModelEntry,
  AnomalyPromptEntry,
  ContributorType,
} from '../models/anomaly-detection/models/AnomalyIncident';

type ContributorEntry = {
  type: ContributorType;
  value: number;
};

const CONTRIBUTOR_TYPE_ORDER: ContributorType[] = ['changed', 'added', 'deleted'];

const getValueSortDirection = (
  type: ContributorType,
  increaseIsPositive: boolean,
  direction: AnomalyDirection,
): 'asc' | 'desc' => {
  if (type === 'deleted') {
    if (increaseIsPositive) {
      return direction === 'positive' ? 'asc' : 'desc';
    }

    return direction === 'positive' ? 'desc' : 'asc';
  }

  if (increaseIsPositive) {
    return direction === 'positive' ? 'desc' : 'asc';
  }

  return direction === 'positive' ? 'asc' : 'desc';
};

export const sortAnomalyContributorEntries = <T extends ContributorEntry>(
  entries: T[],
  increaseIsPositive: boolean,
  direction: AnomalyDirection,
): T[] => {
  return [...entries].sort((left, right) => {
    const leftTypeOrder = CONTRIBUTOR_TYPE_ORDER.indexOf(left.type);
    const rightTypeOrder = CONTRIBUTOR_TYPE_ORDER.indexOf(right.type);

    if (leftTypeOrder !== rightTypeOrder) {
      return leftTypeOrder - rightTypeOrder;
    }

    if (left.type === 'changed') {
      return Math.abs(right.value) - Math.abs(left.value);
    }

    const sortDirection = getValueSortDirection(left.type, increaseIsPositive, direction);
    return sortDirection === 'asc' ? left.value - right.value : right.value - left.value;
  });
};

export const formatAnomalyDeltaValue = (value: number, direction?: AnomalyDirection): string => {
  const prefix = direction === 'positive' ? '+' : direction === 'negative' ? '-' : '';
  const valueAbs = Math.abs(Number(value.toFixed(2)));
  return `${prefix}${valueAbs}`;
};

export const getAnomalyAccentColor = (direction: AnomalyDirection | undefined): string => {
  if (direction === 'positive') return 'var(--green-100)';
  if (direction === 'negative') return 'var(--red-300)';
  return 'var(--grey-ux-400)';
};

export const getAnomalyDirectionIconClass = (
  direction: AnomalyDirection | undefined,
): string | null => {
  if (direction === 'positive') return 'ico--chart-up';
  if (direction === 'negative') return 'ico--chart-down';
  return null;
};

export const getAnomalyContributorIconClass = (type: ContributorType): string => {
  if (type === 'deleted') return 'ico--minus';
  if (type === 'added') return 'ico--plus';
  return 'ico--activity';
};

export const formatAnomalyContributorValue = (
  type: ContributorType,
  value: number,
  unit: string,
  direction?: AnomalyDirection,
): string => {
  if (type === 'changed') {
    return `${formatAnomalyDeltaValue(value, direction)}${unit}`;
  }

  return `${Math.abs(Number(value.toFixed(2)))}${unit}`;
};

export const buildAnomalyPeriodLabel = (
  incident: AnomalyIncident,
  dateKey: string,
  dateFormatter?: (raw: string) => string,
): string => {
  const endDate = incident.endDate ?? dateKey;
  const rawRange =
    incident.startDate && incident.startDate !== endDate
      ? `${incident.startDate} - ${endDate}`
      : endDate;

  return dateFormatter ? dateFormatter(rawRange) : rawRange;
};

export const sortAnomalyPrompts = (
  prompts: AnomalyPromptEntry[] | undefined,
  increaseIsPositive: boolean | undefined,
  direction: AnomalyDirection,
): AnomalyPromptEntry[] => {
  if (!prompts?.length) return [];

  if (increaseIsPositive != null) {
    return sortAnomalyContributorEntries(prompts, increaseIsPositive, direction);
  }

  return [...prompts].sort(
    (left, right) =>
      CONTRIBUTOR_TYPE_ORDER.indexOf(left.type) - CONTRIBUTOR_TYPE_ORDER.indexOf(right.type),
  );
};

export const sortAnomalyModels = (
  models: AnomalyModelEntry[] | undefined,
  increaseIsPositive: boolean | undefined,
  direction: AnomalyDirection,
): AnomalyModelEntry[] => {
  if (!models?.length) return [];

  if (increaseIsPositive != null) {
    return sortAnomalyContributorEntries(models, increaseIsPositive, direction);
  }

  return [...models].sort(
    (left, right) =>
      CONTRIBUTOR_TYPE_ORDER.indexOf(left.type) - CONTRIBUTOR_TYPE_ORDER.indexOf(right.type),
  );
};

export const getAnomalyModelLabel = (model: string): string => {
  return getModelNameLabel(model) || model;
};
